
import { Point } from 'pixi.js';
import App from '../../../vendor_core/core/src/App';
import GameEvents from '../../../vendor_core/core/src/const/GameEvents';
import SoundVars from '../../../vendor_core/core/src/const/SoundVars';
import { Ease } from '../../../vendor_core/core/src/Ease';
import BetModel from '../../../vendor_core/core/src/model/BetModel';
import GameModel from '../../../vendor_core/core/src/model/GameModel';
import SpinModel from '../../../vendor_core/core/src/model/SpinModel';
import { IWinData } from '../../../vendor_core/core/src/server/data/ISpinData';
import { Timeline } from '../../../vendor_core/core/src/Timeline';
import { Tween } from '../../../vendor_core/core/src/Tween';
import { DefaultLayers } from '../../../vendor_core/core/src/utils/Layers';
import Layout from '../../../vendor_core/core/src/utils/Layout';
import SoundController from '../../../vendor_core/core/src/utils/SoundController';
import ViewManager from '../../../vendor_core/core/src/utils/ViewManager';
import { BitmapMoneyField } from '../../../vendor_core/core/src/views/BitmapMoneyField';
import ReelGroup from '../../../vendor_core/core/src/views/ReelGroup';
import Configs from '../../../vendor_core/src/vendorConfigs/Configs';
import VendorWinlinesView from '../../../vendor_core/src/view/VendorWinlinesView';
import GameSoundVars from '../enums/GameSoundVars';

export default class VOEWinlinesView extends VendorWinlinesView {
    // Check jurisdiction if we should play ante win animation and sound
    private disableLowWinCelebration;

    // True for Volatile Egypt (VOE), false for Safari Sun (SPS)
    private useWinLoopSounds: boolean = false;
    private activeSounds: string[] = [];

    constructor() {
        super();

        ReelGroup.instance.on(
            GameEvents.ALL_REELS_STARTED,
            () => this.stopActiveSounds(0.5),
            this
        );
    
        App.dispatcher.on(
            GameEvents.WIN_INTERCEPTED,
            () => this.stopActiveSounds(0.7),
            this
        );
    }

    public highlightWinLine(winLineData: IWinData, removePrevious = true) {
        if (removePrevious) {
            this.winFrameContainer.removeChildren();
        }

        const cfg = {
            symWidth: Layout.scalePixels(this.symBounds.width),
            symHeight: Layout.scalePixels(this.symBounds.height),
            reelGap: Layout.scalePixels(GameModel.gameConfig.reelGap),
            symGap: Layout.scalePixels(GameModel.gameConfig.symGap),
            frameLeft: Layout.scalePixels(GameModel.gameConfig.frameLeft),
            frameTop: Layout.scalePixels(GameModel.gameConfig.frameTop),
            frameBottom: Layout.scalePixels(GameModel.gameConfig.frameBottom),
            frameRight: Layout.scalePixels(GameModel.gameConfig.frameRight),
            beginningOffset: Layout.scalePixels(
                GameModel.gameConfig.beginningOffset
            ),
            winFrameConnectors: GameModel.gameConfig.winFrameConnectors,
            frameConnectorVerticalOffset: Layout.scalePixels(
                GameModel.gameConfig.frameConnectorVerticalOffset
            ),
        };

        if (winLineData.normalLineWin) {
            const lineId = winLineData.lineId;
            const lineSymPositions =
                GameModel.serverGameConfig.paylines[lineId];
            const numWinSyms = winLineData.winSymbols.length;
            const numLineSyms = lineSymPositions.length;

            if (
                GameModel.gameConfig.noWinFrame ||
                (numWinSyms < numLineSyms && this.config.winLineTails)
            ) {

                //HEAD 
                let startPt = new Point();
                let endPt = new Point();
                startPt.x = cfg.beginningOffset;
                endPt.x = startPt.x + (cfg.reelGap + cfg.symWidth * 0.5);
                startPt.y =
                    lineSymPositions[0] *
                    (cfg.symHeight + cfg.symGap) +
                    cfg.symHeight * 0.5;
                endPt.y = startPt.y;

                this.drawLineSegment(startPt, endPt, true);

                let chainedEndpoint = endPt;

                let position = GameModel.gameConfig.noWinFrame
                    ? 0
                    : numWinSyms - 1;
                let endPosition = position + 1;

                //BODY
                while (position < numLineSyms - 1) {
                    startPt.x = chainedEndpoint.x;

                    endPt.x =
                        cfg.beginningOffset +
                        (endPosition * (cfg.symWidth + cfg.reelGap) +
                            cfg.symWidth * 0.5);

                    startPt.y = chainedEndpoint.y;

                    endPt.y =
                        lineSymPositions[endPosition] *
                        (cfg.symHeight + cfg.symGap) +
                        cfg.symHeight * 0.5;

                    this.drawLineSegment(startPt, endPt, true);

                    chainedEndpoint = endPt;

                    position++;
                    endPosition++;
                }

                // TAIL
                startPt = new Point();
                endPt = new Point();
                startPt = chainedEndpoint;
                endPt.x = startPt.x + (cfg.reelGap + cfg.symWidth * 0.5) + cfg.beginningOffset;
                endPt.y = startPt.y;

                this.drawLineSegment(startPt, endPt, false);
            };
        };
    };

    public setWinBannerValue(value: number, animate = false) {
        const config = Configs.game.clientConfig;
        this.disableLowWinCelebration = config.disableLowWinCelebration && SpinModel.getCurrentWin() <= BetModel.totalBet;
        const layer = ViewManager.getLayer(DefaultLayers.PaylineCelebration);
        layer.clear();
        if (SpinModel.hasBigWin() && !SpinModel.respinsRunning) {
            this.playWinSound();
            this.emit(GameEvents.BANNER_ANIM_COMPLETE);
            return;
        }
        
        const scene = layer.setScene("totalWin");
        scene.sceneMoneyFields.forEach((field) => {
            field.visible = false;
        });
        this.winValueBanner = scene.sceneMoneyFields.get(
            this.getWinLevel()
        ) as BitmapMoneyField;
        this.winValueBanner.visible = true;

        let winSpine = scene.spineAnimations.get("totalWins");
        if(!this.disableLowWinCelebration){
            winSpine.play(this.getWinLevel())
        }
        let wholeAnimDuration = winSpine.getAnimDuration(this.getWinLevel());  // 100%
        let blinkValueDuration = (wholeAnimDuration / 100) * this.blinkAnimationPercentages[this.winLevelNames.indexOf(this.getWinLevel())];
        let bangupDuration = wholeAnimDuration - blinkValueDuration;


        if (animate && !this.disableLowWinCelebration) {
            this.useWinLoopSounds && SoundController.playSound("win_loop", true);
            this.winValueBanner.value = 0;
            // const wincountSound = SoundController.playSound(
            //     SoundVars.EFFECT_WINCOUNT
            // );
            //---------start banner grow up-------
            const wvb = this.winValueBanner;

            Tween.to(
                wvb,
                bangupDuration,
                {
                    width: wvb.width * 1.2,
                    height: wvb.height * 1.2,
                    x: wvb.x - ((wvb.width * 1.2 - wvb.width) / 2),
                    y: wvb.y - ((wvb.height * 1.2 - wvb.height) / 2),
                    ease: Ease.None,
                }
            );
            //------------------------------------

            // If the loop sounds are turned off, we need to play the win sound immediately;
            !this.useWinLoopSounds && this.playWinSound();
            this.winValueBannerBangUp.startBangup(
                0,
                value,
                bangupDuration,
                null,
                (currentVal) => {
                    this.winValueBanner.value = currentVal;
                },
                () => {
                    //------final banner popup effect------
                    const wvb = this.winValueBanner;

                    let timeline = new Timeline({
                        autoRemoveChildren: true,
                        onComplete: () => {
                            this.emit(GameEvents.BANNER_ANIM_COMPLETE);
                            this.winValueBanner.visible = false;
                            timeline.clear();
                            timeline.kill();
                        }
                    });
                    timeline
                        .to(wvb,
                            blinkValueDuration / 2,
                            {
                                width: wvb.width * 1.3,
                                height: wvb.height * 1.3,
                                x: wvb.x - ((wvb.width * 1.3 - wvb.width) / 2),
                                y: wvb.y - ((wvb.height * 1.3 - wvb.height) / 2),
                                ease: Ease.None,
                                yoyo: true,
                                repeat: 1
                            }, 0)
                    timeline.play();

                    this.useWinLoopSounds && this.playWinSound();
                    //-------------------------------------
                }
            );
        } else {
            this.winValueBanner.value = value;
            this.playWinSound();
            Tween.delayedCall(wholeAnimDuration, () => {
                this.winValueBanner.visible = false;
            })
            this.emit(GameEvents.BANNER_ANIM_COMPLETE);
        }
        this.winValueBanner.visible = true;
    }

    public playWinSound() {
        if(this.disableLowWinCelebration){
            return;
        }
        
        this.useWinLoopSounds && SoundController.fadeStop("win_loop", 0.5);
        if (SpinModel.hasBigWin()) {
            SoundController.playSound(GameSoundVars.BIG_WIN_PRE);
            return;
        }

        if (!this.winValueBannerBangUp.currentBangup) {
            this.playSounds([SoundVars[SpinModel.getWinsoundName()]]);
        } else {
            /// super.playWinSound();
            if (this.winThresholds) {
                let winSound: string = this.winThresholds[0].name;

                const profit = Math.round(SpinModel.getAccumulatedWin() / BetModel.totalBet);

                this.winThresholds.forEach((threshold) => {
                    if (profit >= threshold.winRatio) {
                        winSound = threshold.name;
                    };
                });

                this.playSounds([GameSoundVars[winSound]]);

            } else {  // use standar win sound
                super.playWinSound();
            }
        }
    }

    /**
     * Plays more than 1 sound and push them to the active sounds.
     *
     * @private
     * @param {string[]} soundIds
     * @memberof VOEWinlinesView
     */
    private playSounds(soundIds: string[]) {
        soundIds.forEach(soundId => {
            if (soundId) {
                SoundController.playSound(soundId);
            }
        });

        this.activeSounds.push(...soundIds);
    }

    /**
     * A simple wrapper for fade stopping more than 1 sound.
     *
     * @private
     * @param {string[]} soundIds
     * @param {number} duration
     * @memberof VOEWinlinesView
     */
    private fadeStopSounds(soundIds: string[], duration: number) {
        soundIds.forEach(soundId => {
            if (soundId) {
                SoundController.fadeStop(soundId, duration);
            }
        })
    }

    private stopActiveSounds(duration: number) {
        this.fadeStopSounds(this.activeSounds, duration);
        this.useWinLoopSounds && SoundController.fadeStop("win_loop", duration);
        this.activeSounds = [];
    }
};
